import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { StoryIllustration } from "@/components/StoryIllustration";
import { STORIES, STORY_THEMES, type StoryTheme } from "@/data/stories";
import { MailchimpForm } from "@/components/MailchimpForm";

const SITE = "https://mrbquotes.com";

export const Route = createFileRoute("/stories/")({
  head: () => ({
    meta: [
      { title: `Mr. B Quotes Stories — Short Essays on Wisdom & Resilience` },
      { name: "description", content: `Master the discipline of clear thinking. Discover an evolving collection of short stories and essays on perspective, resilience, and life's deeper lessons.` },
      { property: "og:title", content: "Mr. B Quotes Stories" },
      { property: "og:description", content: "Short, original stories with life lessons — written to inspire, inform, and make you think." },
      { property: "og:url", content: `${SITE}/stories` },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: `${SITE}/stories` }],
    scripts: [
  {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Mr. B Quotes Stories",
      url: `${SITE}/stories`,
      description: "An evolving collection of short stories and essays on wisdom, resilience, and the discipline of clear thinking.",
    }),
  },
  {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE },
        { "@type": "ListItem", position: 2, name: "Stories", item: `${SITE}/stories` },
      ],
    }),
  },
],
  }),
  component: StoriesIndex,
});

function StoriesIndex() {
  const [theme, setTheme] = useState<StoryTheme | "All">("All");
  // Latest first: newest publication date at the top.
  const sorted = [...STORIES].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  const filtered = theme === "All" ? sorted : sorted.filter((s) => s.theme === theme);

  return (
    <div className="min-h-screen">
      <SiteHeader sub="Quotes · Stories" active="stories" />

      <section className="relative overflow-hidden border-b border-border/60">
        <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-gradient-gold opacity-10 blur-3xl" aria-hidden />
        <div className="mx-auto max-w-6xl px-4 pt-14 pb-12 sm:pt-20 sm:pb-14 relative">
          <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
            <Link to="/" className="hover:text-gold">Home</Link>
            <span className="mx-2 opacity-50">/</span>
            <span className="text-foreground">Stories</span>
          </nav>
          <div className="mt-4 text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Original short essays</div>
          <h1 className="mt-2 font-display text-5xl sm:text-7xl leading-[0.95]">
            Mr. B Quotes <span className="text-gold italic">Stories</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base sm:text-lg text-muted-foreground leading-relaxed">
            {STORIES.length} short, original essays — each a small story with a long lesson. Written to inspire, inform, and make you think, think, think.
          </p>

          <div className="mt-8 flex flex-wrap gap-2">
            <button
              onClick={() => setTheme("All")}
              className={`rounded-full px-4 py-1.5 text-xs uppercase tracking-widest transition border ${theme === "All" ? "border-gold bg-gold/10 text-gold" : "border-border text-muted-foreground hover:border-gold hover:text-gold"}`}
            >
              All themes
            </button>
            {STORY_THEMES.map((t) => (
              <button
                key={t}
                onClick={() => setTheme(t)}
                className={`rounded-full px-4 py-1.5 text-xs uppercase tracking-widest transition border ${theme === t ? "border-gold bg-gold/10 text-gold" : "border-border text-muted-foreground hover:border-gold hover:text-gold"}`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-6xl px-4 py-12 sm:py-16">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((s) => (
            <Link
              key={s.slug}
              to="/stories/$slug"
              params={{ slug: s.slug }}
              className="group overflow-hidden rounded-2xl border border-border bg-card transition hover:border-gold hover:shadow-elegant"
            >
              <div className="aspect-[5/3] overflow-hidden">
                <StoryIllustration slug={s.slug} title={s.title} theme={s.theme} variant="card" className="h-full w-full transition group-hover:scale-[1.02]" />
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-muted-foreground">
                  <span className="text-gold">{s.theme}</span>
                  <span>{new Date(s.date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                </div>
                <h2 className="mt-2 font-display text-2xl text-foreground group-hover:text-gold transition">{s.title}</h2>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed line-clamp-3">{s.dek}</p>
                <div className="mt-4 text-xs font-medium text-gold opacity-0 group-hover:opacity-100 transition">Read the story →</div>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-16 sm:mt-20 max-w-2xl mx-auto">
          <MailchimpForm />
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
